import { memo } from 'react';
import Highlighter from 'react-highlight-words';
import styled from 'styled-components';
import type { TextProps } from '@frameio/vapor';
import { getTextColor, Text } from '@frameio/vapor';
import { theme } from '@frameio/vapor-theme';

type HighlightTextProps = {
  text: string;
  highlights?: string | string[];
  className?: string;
  truncate?: boolean;
} & Partial<Pick<TextProps, 'color' | 'variant' | 'fontWeight'>>;

export const HighlightText = memo(function HighlightText({
  className = '',
  color = 'body',
  fontWeight = 'medium',
  highlights = [],
  text,
  truncate = true,
  variant = 'primary',
}: HighlightTextProps) {
  return (
    <HighlightTextContainer aria-label={text} className={className} role="text">
      <TextContainer
        $color={color ?? 'body'}
        $fontWeight={fontWeight ?? 'medium'}
        enableTooltip={true}
        truncate={truncate}
        variant={variant}
      >
        <Highlighter
          autoEscape={true}
          highlightClassName="highlight"
          searchWords={
            typeof highlights === 'string' ? [highlights] : highlights
          }
          textToHighlight={text}
        />
      </TextContainer>
    </HighlightTextContainer>
  );
});

HighlightText.displayName = 'HighlightText';

const HighlightTextContainer = styled.div`
  max-width: 100%;
  min-width: 0;
  width: 100%;
`;

const TextContainer = styled(Text)<{
  $color: NonNullable<TextProps['color']>;
  $fontWeight: NonNullable<TextProps['fontWeight']>;
}>`
  border-radius: ${theme.radii[4]};
  color: ${(p) => {
    if (p.$color === 'inherit') {
      return 'inherit';
    }

    return getTextColor(p.$color) ?? p.$color;
  }};
  font-weight: ${(p) => theme.fontWeights[p.$fontWeight]};
  margin: 0px -2px;
  max-width: 100%;
  padding: 0px 2px;
  width: 100%;

  [role='tooltip'][data-position] & {
    font-weight: ${theme.fontWeights.regular};
  }

  & > span {
    display: inline;
    white-space: inherit;
  }

  & > span > span {
    display: inline;
    position: relative;
    white-space: inherit;
    z-index: 2;
  }

  @media (min-width: ${theme.breakpoints.base}) {
    margin: 2px -2px;
  }

  mark.highlight {
    background-color: ${theme.colors.components.textHighlight.background
      .focusMedium};
    border-radius: ${theme.radii[4]};
    color: ${theme.colors.text.primary};
    display: inline;
    margin: 0px -1px;
    padding: 0px 1px 1px;
    position: relative;
    white-space: inherit;
    z-index: 1;
  }
`;
